"use client";

import { useCallback } from "react";

type ScrollBehaviorOption = "auto" | "smooth";

interface ScrollToTopOptions {
  behavior?: ScrollBehaviorOption;
  container?: Element | null | string;
}

export const useScrollToTop = () => {
  const scrollToTop = useCallback((options?: ScrollToTopOptions) => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const behavior: ScrollBehaviorOption = options?.behavior ?? "smooth";

    const isScrollable = (el: Element | null): el is HTMLElement => {
      if (!el || !(el instanceof HTMLElement)) return false;
      const style = window.getComputedStyle(el);
      const overflowY = style?.overflowY;
      const canScroll = el.scrollHeight > el.clientHeight;
      return (overflowY === "auto" || overflowY === "scroll") && canScroll;
    };

    const resolveContainer = (): HTMLElement | null => {
      const provided = options?.container;
      if (typeof provided === "string") {
        const el = document.querySelector(provided);
        if (el instanceof HTMLElement) return el;
      } else if (provided instanceof HTMLElement) {
        return provided;
      }

      // Try to find the nearest scrollable ancestor from the active element
      let current: Element | null = document.activeElement as Element | null;
      while (current && current !== document.body && current !== document.documentElement) {
        if (isScrollable(current)) return current as HTMLElement;
        current = current.parentElement;
      }

      // Fallbacks: document.scrollingElement, documentElement, body
      const scrollingEl = (document.scrollingElement || document.documentElement || document.body) as HTMLElement | null;
      if (scrollingEl) return scrollingEl;
      return null;
    };

    const target = resolveContainer();

    if (target) {
      try {
        // Prefer element-level scrolling when available
        (target as any).scrollTo?.({ top: 0, behavior: behavior as ScrollBehavior });
        // Ensure sync fallback in case method not present
        if (target.scrollTop !== 0) target.scrollTop = 0;
      } catch {
        try {
          target.scrollTop = 0;
        } catch {
          // ignore
        }
      }
    }

    // Always try window as a final fallback
    try {
      window.scrollTo({ top: 0, behavior: behavior as ScrollBehavior });
    } catch {
      window.scrollTo(0, 0);
    }

    // Authorised app shell: scroll container is `.layout-visible-scrollbar` or `.layout-section`
    const layoutMain = document.querySelector(".layout-visible-scrollbar, .layout-section");
    if (layoutMain instanceof HTMLElement) {
      try {
        layoutMain.scrollTo({ top: 0, behavior: behavior as ScrollBehavior });
        if (layoutMain.scrollTop !== 0) layoutMain.scrollTop = 0;
      } catch {
        try {
          layoutMain.scrollTop = 0;
        } catch {
          // ignore
        }
      }
    }
  }, []);

  return scrollToTop;
};

export default useScrollToTop;


